// ./pages/HowItWorks.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield, Globe, Camera, Bell, Clock, CheckCircle,
  XCircle, AlertTriangle, Wifi, Lock, Activity,
  ChevronDown, ChevronUp, Zap, Gift, Calendar, Server
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';
import './HowItWorks.css';

function HowItWorks({ user, onLogout }) {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const checkLayers = [
    {
      icon: Globe,
      color: '#4f46e5',
      bg: '#eff6ff',
      title: t('how.checks.dns.title'),
      desc: t('how.checks.dns.desc'),
    },
    {
      icon: Wifi,
      color: '#0891b2',
      bg: '#ecfeff',
      title: t('how.checks.tcp.title'),
      desc: t('how.checks.tcp.desc'),
    },
    {
      icon: Lock,
      color: '#7c3aed',
      bg: '#f5f3ff',
      title: t('how.checks.ssl.title'),
      desc: t('how.checks.ssl.desc'),
    },
    {
      icon: Activity,
      color: '#059669',
      bg: '#ecfdf5',
      title: t('how.checks.http.title'),
      desc: t('how.checks.http.desc'),
    },
  ];

  const sslFields = [
    { icon: Clock,    color: '#7c3aed', label: t('how.ssl.field_days') },
    { icon: Calendar, color: '#dc2626', label: t('how.ssl.field_expiry') },
    { icon: CheckCircle, color: '#059669', label: t('how.ssl.field_last_check') },
  ];

  const domainFields = [
    { icon: Clock,    color: '#7c3aed', label: t('how.domain_info.field_days') },
    { icon: Calendar, color: '#dc2626', label: t('how.domain_info.field_expiry') },
    { icon: CheckCircle, color: '#059669', label: t('how.domain_info.field_last_check') },
    { icon: Server,   color: '#0891b2', label: t('how.domain_info.field_registrar') },
  ];

  const statusCodes = [
    { code: '200',     label: t('how.status.200'), color: '#059669', bg: '#d1fae5' },
    { code: '301/302', label: t('how.status.301'), color: '#d97706', bg: '#fef3c7' },
    { code: '4xx',     label: t('how.status.4xx'), color: '#dc2626', bg: '#fee2e2' },
    { code: '5xx',     label: t('how.status.5xx'), color: '#9333ea', bg: '#f3e8ff' },
    { code: '0',       label: t('how.status.0'),   color: '#64748b', bg: '#f1f5f9' },
  ];

  const screenshotRules = [
    { icon: CheckCircle, color: '#059669', text: t('how.screenshot.when.new_error') },
    { icon: CheckCircle, color: '#059669', text: t('how.screenshot.when.error_changed') },
    { icon: XCircle,     color: '#dc2626', text: t('how.screenshot.skip.same_error') },
    { icon: XCircle,     color: '#dc2626', text: t('how.screenshot.skip.network_fail') },
    { icon: XCircle,     color: '#dc2626', text: t('how.screenshot.skip.cached') },
  ];

  const faqs = [
    { q: t('how.faq.trial_q'),      a: t('how.faq.trial_a') },
    { q: t('how.faq.notif_q'),      a: t('how.faq.notif_a') },
    { q: t('how.faq.check_freq_q'), a: t('how.faq.check_freq_a') },
    { q: t('how.faq.screenshot_q'), a: t('how.faq.screenshot_a') },
    { q: t('how.faq.uptime_q'),     a: t('how.faq.uptime_a') },
    { q: t('how.faq.ssl_q'),        a: t('how.faq.ssl_a') },
  ];

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <h1>{t('how.title')}</h1>
        </header>

        <div className="hiw-page">

          {/* Hero */}
          <div className="hiw-hero">
            <div className="hiw-hero-icon">
              <Zap size={28} strokeWidth={1.5} color="#4f46e5" />
            </div>
            <p className="hiw-hero-sub">{t('how.subtitle')}</p>
          </div>

          {/* Trial */}
          <div className="hiw-trial-card">
            <div className="hiw-trial-icon">
              <Gift size={22} strokeWidth={1.5} color="#7c3aed" />
            </div>
            <div className="hiw-trial-content">
              <h3 className="hiw-trial-title">{t('how.trial.title')}</h3>
              <p className="hiw-trial-text">{t('how.trial.text')}</p>
              <div className="hiw-trial-badges">
                <span className="hiw-badge purple">{t('how.trial.badge1')}</span>
                <span className="hiw-badge indigo">{t('how.trial.badge2')}</span>
              </div>
            </div>
          </div>

          {/* Kontrol Döngüsü */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Clock size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.cycle.title')}</h2>
            </div>
            <p className="hiw-section-desc">{t('how.cycle.desc')}</p>
            <div className="hiw-timeline">
              {[
                t('how.cycle.step1'),
                t('how.cycle.step2'),
                t('how.cycle.step3'),
                t('how.cycle.step4'),
              ].map((label, i) => (
                <div className="hiw-timeline-item" key={i}>
                  <div className="hiw-timeline-dot">{i + 1}</div>
                  <p className="hiw-timeline-label">{label}</p>
                </div>
              ))}
            </div>

            {/* Uptime notu */}
            <div className="hiw-uptime-note">
              <AlertTriangle size={15} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0 }} />
              <span>{t('how.cycle.uptime_note')}</span>
            </div>
          </div>

          {/* Katmanlı Kontroller */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Shield size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.checks.title')}</h2>
            </div>
            <p className="hiw-section-desc">{t('how.checks.desc')}</p>
            <div className="hiw-checks-grid">
              {checkLayers.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div className="hiw-check-card" key={i} style={{ '--card-color': c.color, '--card-bg': c.bg }}>
                    <div className="hiw-check-icon">
                      <Icon size={20} strokeWidth={1.5} color={c.color} />
                    </div>
                    <div>
                      <p className="hiw-check-title">{c.title}</p>
                      <p className="hiw-check-desc">{c.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SSL Takibi */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Lock size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.ssl.title')}</h2>
            </div>
            <p className="hiw-section-desc">{t('how.ssl.desc')}</p>
            <div className="hiw-info-fields">
              {sslFields.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div className="hiw-info-field" key={i}>
                    <div className="hiw-info-field-icon" style={{ background: f.color + '18' }}>
                      <Icon size={16} strokeWidth={1.5} color={f.color} />
                    </div>
                    <span>{f.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Domain Kayıt Bilgisi */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Globe size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.domain_info.title')}</h2>
            </div>
            <p className="hiw-section-desc">{t('how.domain_info.desc')}</p>
            <div className="hiw-info-fields">
              {domainFields.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div className="hiw-info-field" key={i}>
                    <div className="hiw-info-field-icon" style={{ background: f.color + '18' }}>
                      <Icon size={16} strokeWidth={1.5} color={f.color} />
                    </div>
                    <span>{f.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* HTTP Durum Kodları */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Activity size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.status.title')}</h2>
            </div>
            <p className="hiw-section-desc">{t('how.status.desc')}</p>
            <div className="hiw-status-list">
              {statusCodes.map((s, i) => (
                <div className="hiw-status-item" key={i}>
                  <span className="hiw-status-code" style={{ background: s.bg, color: s.color }}>{s.code}</span>
                  <span className="hiw-status-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Ekran Görüntüsü */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Camera size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.screenshot.title')}</h2>
            </div>
            <p className="hiw-section-desc">{t('how.screenshot.desc')}</p>
            <div className="hiw-screenshot-rules">
              {screenshotRules.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div className="hiw-rule-item" key={i}>
                    <Icon size={17} strokeWidth={1.5} color={r.color} />
                    <span>{r.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bildirimler */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <Bell size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.notif.title')}</h2>
            </div>
            <div className="hiw-notif-grid">
              <div className="hiw-notif-card green">
                <CheckCircle size={18} strokeWidth={1.5} color="#059669" style={{ flexShrink: 0 }} />
                <div>
                  <p className="hiw-notif-label">{t('how.notif.when')}</p>
                  <p className="hiw-notif-text">{t('how.notif.when_text')}</p>
                </div>
              </div>
              <div className="hiw-notif-card red">
                <XCircle size={18} strokeWidth={1.5} color="#dc2626" style={{ flexShrink: 0 }} />
                <div>
                  <p className="hiw-notif-label">{t('how.notif.skip')}</p>
                  <p className="hiw-notif-text">{t('how.notif.skip_text')}</p>
                </div>
              </div>
              <div className="hiw-notif-card yellow">
                <AlertTriangle size={18} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0 }} />
                <div>
                  <p className="hiw-notif-label">{t('how.notif.settings')}</p>
                  <p className="hiw-notif-text">{t('how.notif.settings_text')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* SSS */}
          <div className="hiw-section">
            <div className="hiw-section-header">
              <AlertTriangle size={18} strokeWidth={1.5} color="#4f46e5" />
              <h2 className="hiw-section-title">{t('how.faq.title')}</h2>
            </div>
            <div className="hiw-faq-list">
              {faqs.map((f, i) => (
                <div className={`hiw-faq-item ${openFaq === i ? 'open' : ''}`} key={i}>
                  <button className="hiw-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span>{f.q}</span>
                    {openFaq === i
                      ? <ChevronUp size={16} color="#6b7280" />
                      : <ChevronDown size={16} color="#6b7280" />}
                  </button>
                  {openFaq === i && <div className="hiw-faq-a">{f.a}</div>}
                </div>
              ))}
            </div>
          </div>
          <div classname="contact-admin" style={{display: 'flex', justifyContent: 'center'}}>
            If you have any questions, reach out at <span style={{color:'#6366f1', textDecoration:'none', fontWeight: '500', marginLeft: '0.2rem'}}> support@ruxengine.com</span>
          </div>
        </div>
      </main>
    </div>
  );
}

export default HowItWorks;
